import dayjs from "dayjs";

/**
 * Normalizes date string to DD/MM/YYYY format.
 * Handles various input formats and validates the date.
 */
function normalizeDate(dateValue: unknown): string | null {
  if (typeof dateValue !== "string" || !dateValue.trim()) {
    return null;
  }

  const trimmed = dateValue.trim();

  // If already in DD/MM/YYYY format and valid, return as-is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    if (dayjs(trimmed, "DD/MM/YYYY", true).isValid()) {
      return trimmed;
    }
  }

  // Try parsing with various formats
  const formats = [
    "YYYY-MM-DD",
    "DD-MM-YYYY",
    "MM/DD/YYYY",
    "YYYY/MM/DD",
    "D/M/YYYY",
    "DD/MM/YY",
    "D/M/YY",
    "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO format with time
    "YYYY-MM-DDTHH:mm:ssZ",
    "YYYY-MM-DDTHH:mm:ss",
  ];

  for (const format of formats) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }
  }

  // Try dayjs auto-parsing as fallback (less strict)
  const autoParsed = dayjs(trimmed);
  if (autoParsed.isValid()) {
    return autoParsed.format("DD/MM/YYYY");
  }

  return null;
}

/**
 * Handles all breaking schema changes for invoice data migration.
 *
 * This function handles multiple breaking changes:
 * 1. invoiceNumber (string) -> invoiceNumberObject (object)
 * 2. receiptNumber (string) -> receiptNumber (object with series, number, etc.)
 * 3. Removes deprecated buyer.phone and buyer.phoneFieldIsVisible fields
 * 4. Cleans up invalid email addresses
 * 5. Strips all deprecated visibility toggle fields and other removed fields
 * 6. Normalizes dates to DD/MM/YYYY format
 *
 * @example
 * ```typescript
 * const json = {
 *   invoiceNumber: "123",
 *   receiptNumber: "R-001",
 *   buyer: { phone: "123", phoneFieldIsVisible: true }
 * };
 * const updatedJson = handleInvoiceNumberBreakingChange(json);
 * // Returns migrated data matching current schema
 * ```
 */
export function handleInvoiceNumberBreakingChange(json: unknown): unknown {
  if (typeof json !== "object" || json === null) {
    return json;
  }

  const newJson = { ...(json as Record<string, unknown>) };

  // ============================================
  // 1. Normalize dateOfIssue to DD/MM/YYYY format
  // ============================================
  if ("dateOfIssue" in newJson) {
    const normalizedDate = normalizeDate(newJson.dateOfIssue);
    if (normalizedDate) {
      newJson.dateOfIssue = normalizedDate;
    } else {
      // If date is invalid, set to today's date
      newJson.dateOfIssue = dayjs().format("DD/MM/YYYY");
    }
  } else {
    // If dateOfIssue is missing entirely, set to today's date
    newJson.dateOfIssue = dayjs().format("DD/MM/YYYY");
  }

  // ============================================
  // 2. Migrate invoiceNumber (string) -> invoiceNumberObject (object)
  // ============================================
  if ("invoiceNumber" in newJson && typeof newJson.invoiceNumber === "string") {
    const invoiceNumberLabel = "מספר חשבונית";
    newJson.invoiceNumberObject = {
      label: invoiceNumberLabel + ":",
      value: newJson.invoiceNumber,
    };
    delete newJson.invoiceNumber;
  }

  // ============================================
  // 3. Migrate receiptNumber (string) -> receiptNumber (object)
  // ============================================
  if ("receiptNumber" in newJson && typeof newJson.receiptNumber === "string") {
    const receiptNumStr = newJson.receiptNumber as string;

    // Try to extract series prefix and number
    // Examples: "R-001" -> series: "R-", number: "001"
    //           "2025-0001" -> series: "2025-", number: "0001"
    //           "123" -> series: "", number: "123"
    const match = receiptNumStr.match(/^([^0-9]*)(\d+)$/);

    newJson.receiptNumber = {
      series: match ? match[1] : "",
      number: receiptNumStr,
      seriesFieldIsVisible: true,
      numberFieldIsVisible: true,
    };

    // Remove deprecated lastNumber if present
    delete (newJson.receiptNumber as Record<string, unknown>).lastNumber;
  }

  // ============================================
  // 4. Remove deprecated seller fields
  // ============================================
  if (
    "seller" in newJson &&
    typeof newJson.seller === "object" &&
    newJson.seller !== null
  ) {
    const seller = { ...(newJson.seller as Record<string, unknown>) };

    // List of deprecated fields that no longer exist in schema
    const deprecatedSellerFields = [
      "phoneFieldIsVisible",
      "vatNoLabelText",
      "vatNoFieldIsVisible",
      "email",
      "emailFieldIsVisible",
      "accountNumber",
      "accountNumberFieldIsVisible",
      "swiftBic",
      "swiftBicFieldIsVisible",
      "notes",
      "notesFieldIsVisible",
      "nameFieldIsVisible",
      "addressFieldIsVisible",
      "businessTypeFieldIsVisible",
    ];

    for (const field of deprecatedSellerFields) {
      if (field in seller) {
        delete seller[field];
      }
    }

    newJson.seller = seller;
  }

  // ============================================
  // 5. Remove deprecated buyer fields
  // ============================================
  if (
    "buyer" in newJson &&
    typeof newJson.buyer === "object" &&
    newJson.buyer !== null
  ) {
    const buyer = { ...(newJson.buyer as Record<string, unknown>) };

    // List of deprecated fields that no longer exist in schema
    const deprecatedBuyerFields = [
      "phone",
      "phoneFieldIsVisible",
      "address",
      "addressFieldIsVisible",
      "vatNoLabelText",
      "vatNoFieldIsVisible",
      "email",
      "emailFieldIsVisible",
      "notes",
      "notesFieldIsVisible",
      "nameFieldIsVisible",
    ];

    for (const field of deprecatedBuyerFields) {
      if (field in buyer) {
        delete buyer[field];
      }
    }

    newJson.buyer = buyer;
  }

  // ============================================
  // 6. Remove deprecated fields from invoice items
  // ============================================
  if ("items" in newJson && Array.isArray(newJson.items)) {
    const deprecatedItemFields = [
      "typeOfGTU",
      "typeOfGTUFieldIsVisible",
      "amountFieldIsVisible",
      "unit",
      "unitFieldIsVisible",
      "netPriceFieldIsVisible",
      "vat",
      "vatFieldIsVisible",
      "preTaxAmountFieldIsVisible",
      "grossAmount",
      "grossAmountFieldIsVisible",
    ];

    newJson.items = newJson.items.map((item: unknown) => {
      if (typeof item !== "object" || item === null) {
        return item;
      }
      const newItem = { ...(item as Record<string, unknown>) };

      for (const field of deprecatedItemFields) {
        if (field in newItem) {
          delete newItem[field];
        }
      }

      return newItem;
    });
  }

  // ============================================
  // 7. Remove deprecated top-level fields
  // ============================================
  const deprecatedTopLevelFields = [
    "invoiceNumberObject",
    "invoiceNumberFieldIsVisible",
    "invoiceEmailRecipient",
    "invoiceEmailCompanyTo",
    "dateOfIssueFieldIsVisible",
    "dateFormatFieldIsVisible",
    "currencyFieldIsVisible",
    "totalFieldIsVisible",
    "totalVatFieldIsVisible",
    "totalPreTaxFieldIsVisible",
    "sellerFieldIsVisible",
    "buyerFieldIsVisible",
    "itemsFieldIsVisible",
    "paymentMethodFieldIsVisible",
    "logoFieldIsVisible",
    "businessTypeFieldIsVisible",
    "includeInvoicePdf",
    "vatTotal",
    "preTaxAmountTotal",
    "signature",
    "signatureFieldIsVisible",
  ];

  for (const field of deprecatedTopLevelFields) {
    if (field in newJson) {
      delete newJson[field];
    }
  }

  // ============================================
  // 8. Clean up invalid email addresses (convert to empty string)
  // ============================================
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Helper to clean email fields
  const cleanEmail = (obj: Record<string, unknown>, field: string) => {
    if (field in obj && typeof obj[field] === "string") {
      const email = obj[field] as string;
      if (email && !emailRegex.test(email)) {
        obj[field] = "";
      }
    }
  };

  // Clean seller.email
  if (
    "seller" in newJson &&
    typeof newJson.seller === "object" &&
    newJson.seller !== null
  ) {
    cleanEmail(newJson.seller as Record<string, unknown>, "email");
  }

  // Clean buyer.email
  if (
    "buyer" in newJson &&
    typeof newJson.buyer === "object" &&
    newJson.buyer !== null
  ) {
    cleanEmail(newJson.buyer as Record<string, unknown>, "email");
  }

  // Clean top-level email fields
  cleanEmail(newJson, "invoiceEmailRecipient");
  cleanEmail(newJson, "invoiceEmailCompanyTo");

  // ============================================
  // 9. Clean up invalid phone numbers in seller
  // ============================================
  if (
    "seller" in newJson &&
    typeof newJson.seller === "object" &&
    newJson.seller !== null
  ) {
    const seller = newJson.seller as Record<string, unknown>;
    if ("phone" in seller && typeof seller.phone === "string") {
      const phone = seller.phone as string;
      // Israeli phone regex - if invalid, clear it
      const israeliPhoneRegex =
        /^(\+972[-\s]?|0)([234589]|5[0-9])[-\s]?\d{3}[-\s]?\d{4}$/;
      if (phone && !israeliPhoneRegex.test(phone.replace(/\s/g, ""))) {
        seller.phone = "";
      }
    }
  }

  return newJson;
}
